import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';

import EmailSignUpModal from './EmailSignUpModal';
import PhoneVerificationModal from '../modal/PhoneVerificationModal';
import AccountLinkingModal from '../modal/AccountLinkingModal';
import Iconify from '../iconify';
import EmailSignInModal from './EmailSignInModal';
import ForgotPasswordModal from './ForgotPasswordModal';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 420 },
  maxWidth: 420,
  bgcolor: 'background.paper',
  border: '1px solid #FFFF',
  boxShadow: 24,
  borderRadius: 3,
  p: 4,
};

const socialButtonStyle = {
  width: '100%',
  height: 52,
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 500,
  justifyContent: 'flex-start',
  px: 3,
  border: '1px solid #e0e0e0',
  '&:hover': {
    bgcolor: '#f5f5f5',
    border: '1px solid #d0d0d0',
  },
};

export default function SocialLoginModal({
  open,
  handleClose,
  googleLogin,
  appleLogin,
  onAccountCreated,
  requirePhoneVerification = true,
  onOpenInviteRedemption,
}) {
  const [openSignUp, setOpenSignUp] = React.useState(false);
  const [openPhoneVerification, setOpenPhoneVerification] = React.useState(false);
  const [pendingUserData, setPendingUserData] = React.useState(null);
  const [loadingGoogle, setLoadingGoogle] = React.useState(false);
  const [loadingApple, setLoadingApple] = React.useState(false);

  // Email auth branch. `authEmail` is lifted so an address typed on the
  // sign-in form survives the hop to forgot-password and back, and so
  // EmailSignUpModal can hand over an address that turned out to be taken.
  const [openSignIn, setOpenSignIn] = React.useState(false);
  const [openForgot, setOpenForgot] = React.useState(false);
  const [authEmail, setAuthEmail] = React.useState('');

  // Account linking modal state
  const [accountLinkingOpen, setAccountLinkingOpen] = React.useState(false);
  const [accountLinkingData, setAccountLinkingData] = React.useState({
    email: '',
    existingProvider: '',
    newProvider: '',
  });

  const handleOpenSignUp = () => setOpenSignUp(true);
  const handleCloseSignUp = () => setOpenSignUp(false);

  const handleOpenPhoneVerification = () => setOpenPhoneVerification(true);
  const handleClosePhoneVerification = () => {
    setOpenPhoneVerification(false);
    setPendingUserData(null);
  };

  const handleCloseAccountLinking = () => {
    setAccountLinkingOpen(false);
    setAccountLinkingData({ email: '', existingProvider: '', newProvider: '' });
  };

  // Google/Apple may come back with { needsLink } instead of a uid (one
  // email, one account). Open AccountLinkingModal; onLinked re-enters here.
  const applySignedIn = (result) => {
    if (!result) return;
    if (requirePhoneVerification && !result.phoneVerified) {
      setPendingUserData(result);
      handleClose();
      handleOpenPhoneVerification();
    } else {
      onAccountCreated?.(result);
      handleClose();
    }
  };

  const openLinking = (result) => {
    setAccountLinkingData({
      email: result.email || '',
      existingProvider: '',
      newProvider: result.newProvider || '',
    });
    setAccountLinkingOpen(true);
  };

  const handleGoogleLogin = async () => {
    setLoadingGoogle(true);
    try {
      const result = await googleLogin();
      if (result?.needsLink) openLinking(result);
      else if (result?.uid) applySignedIn(result);
    } catch (error) {
      console.error('Google login error:', error);
    }
    setLoadingGoogle(false);
  };

  const handleAppleLogin = async () => {
    setLoadingApple(true);
    try {
      const result = await appleLogin();
      if (result?.needsLink) openLinking(result);
      else if (result?.uid) applySignedIn(result);
    } catch (error) {
      console.error('Apple login error:', error);
    }
    setLoadingApple(false);
  };

  // Does not call handleClose: the chooser stays open underneath, hidden by
  // the condition on its own Modal, so "back" can reveal it again.
  const handleOpenSignIn = () => setOpenSignIn(true);

  // Leaves the email branch entirely, revealing the chooser.
  const handleBackToChooser = () => {
    setOpenSignIn(false);
    setAuthEmail('');
  };

  const handleForgotPassword = (email) => {
    setAuthEmail(email || '');
    setOpenSignIn(false);
    setOpenForgot(true);
  };

  const handleBackToSignIn = () => {
    setOpenForgot(false);
    setOpenSignIn(true);
  };

  const handleSignInInstead = (email) => {
    setAuthEmail(email || '');
    handleCloseSignUp();
    setOpenSignIn(true);
  };

  const handleResetFromSignUp = (email) => {
    setAuthEmail(email || '');
    handleCloseSignUp();
    setOpenForgot(true);
  };

  // The one place deciding whether phone verification stands between a
  // freshly authenticated member and onAccountCreated. Email sign-up and
  // sign-in both come through here, so they cannot drift apart on the rule.
  //
  // `needsVerification` is an explicit argument rather than a defaulted one.
  // Email sign-up reaches this indirectly, as EmailSignUpModal's
  // `onAccountCreated` prop, and a default would be silently overridden the day
  // that call gained a second argument — a falsy one, like a map index, would
  // skip verification with nothing to catch it.
  const routeAfterAuth = (userData, needsVerification) => {
    if (needsVerification) {
      setPendingUserData(userData);
      handleCloseSignUp();
      handleOpenPhoneVerification();
    } else {
      onAccountCreated(userData);
      handleCloseSignUp();
      handleClose();
    }
  };

  // Email sign-up always creates a brand-new account, so the prop alone
  // decides. Deliberately takes one argument and ignores anything else it is
  // handed, which is what makes the arity above safe.
  const handleSignUpWithVerification = (userData) =>
    routeAfterAuth(userData, requirePhoneVerification);

  // An existing member may already have verified, so the backend's answer for
  // this specific person also has to agree before we ask again.
  const handleSignedIn = (result) => {
    setOpenSignIn(false);
    setAuthEmail('');
    // The chooser is still open underneath at this point — end the whole
    // flow, or it reappears behind a member who has just signed in.
    handleClose();
    routeAfterAuth(result.user, requirePhoneVerification && result.requiresPhoneVerification);
  };

  const handlePhoneVerificationComplete = (verifiedUserData) => {
    onAccountCreated(verifiedUserData);
    handleClosePhoneVerification();
    handleClose();
  };

  return (
    <>
      {/* The chooser hides, rather than closes, while a child is up — so a
          back arrow has something to come back to. `open` is the parent's
          to control; only a completed sign-in or dismissing the flow
          should actually end it. */}
      <Modal
        open={
          open &&
          !openSignIn &&
          !openForgot &&
          !openSignUp &&
          !openPhoneVerification &&
          !accountLinkingOpen
        }
        onClose={handleClose}
        aria-labelledby="social-login-title"
        aria-describedby="social-login-description"
      >
        <Box sx={style} data-testid="social-login-modal">
          {/* Header */}
          <Box textAlign="center" mb={3}>
            <Typography
              id="social-login-title"
              variant="h5"
              component="h2"
              fontWeight={700}
              color="#242258"
              mb={1}
            >
              Welcome to KBT
            </Typography>
            <Typography
              id="social-login-description"
              variant="body2"
              color="text.secondary"
            >
              Sign in or create an account to continue
            </Typography>
          </Box>

          {/* Google Sign In */}
          <Box mb={2}>
            <Button
              onClick={handleGoogleLogin}
              disabled={loadingGoogle || loadingApple}
              aria-label="Sign in with Google"
              aria-busy={loadingGoogle}
              data-testid="google-signin"
              sx={{
                ...socialButtonStyle,
                color: '#333',
                bgcolor: 'white',
              }}
            >
              {loadingGoogle ? (
                <CircularProgress size={20} sx={{ mr: 2 }} aria-hidden="true" />
              ) : (
                <Box
                  component="img"
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt=""
                  aria-hidden="true"
                  sx={{ width: 20, height: 20, mr: 2 }}
                />
              )}
              Continue with Google
            </Button>
          </Box>

          {/* Apple Sign In */}
          <Box mb={2}>
            <Button
              onClick={handleAppleLogin}
              disabled={loadingGoogle || loadingApple}
              aria-label="Sign in with Apple"
              aria-busy={loadingApple}
              data-testid="apple-signin"
              sx={{
                ...socialButtonStyle,
                color: 'white',
                bgcolor: '#000',
                '&:hover': {
                  bgcolor: '#333',
                },
              }}
            >
              {loadingApple ? (
                <CircularProgress size={20} sx={{ mr: 2, color: 'white' }} aria-hidden="true" />
              ) : (
                <Iconify icon="ic:baseline-apple" width={24} sx={{ mr: 2 }} aria-hidden="true" />
              )}
              Continue with Apple
            </Button>
          </Box>

          {/* Email Sign In */}
          <Box mb={2}>
            <Button
              onClick={handleOpenSignIn}
              disabled={loadingGoogle || loadingApple}
              aria-label="Sign in with email and password"
              data-testid="email-signin"
              sx={{
                ...socialButtonStyle,
                color: '#333',
                bgcolor: 'white',
              }}
            >
              <Iconify
                icon="eva:email-outline"
                width={20}
                sx={{ mr: 2, color: '#242258' }}
                aria-hidden="true"
              />
              Sign in with email
            </Button>
          </Box>

          {/* Divider */}
          <Box my={3}>
            <Divider>
              <Typography variant="body2" color="text.secondary" px={2}>
                or
              </Typography>
            </Divider>
          </Box>

          {/* Email sign up */}
          <Box>
            <Button
              onClick={handleOpenSignUp}
              disabled={loadingGoogle || loadingApple}
              aria-label="Create an account with email and password"
              data-testid="signup-open"
              sx={{
                ...socialButtonStyle,
                color: 'white',
                bgcolor: '#242258',
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: '#1a1a40',
                },
              }}
            >
              <Iconify icon="eva:person-outline" width={24} sx={{ mr: 2 }} aria-hidden="true" />
              Create an account
            </Button>
          </Box>

          {/* KBT-174: entry point for members who received an admin invite email */}
          {onOpenInviteRedemption && (
            <Box mt={2} textAlign="center">
              <Typography
                component="button"
                type="button"
                onClick={() => {
                  handleClose();
                  onOpenInviteRedemption();
                }}
                data-testid="open-invite-redemption"
                variant="body2"
                sx={{
                  background: 'none',
                  border: 'none',
                  color: '#242258',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  p: 0,
                }}
              >
                Have an invite code? Redeem it
              </Typography>
            </Box>
          )}

          {/* Phone verification notice */}
          {requirePhoneVerification && (
            <Box
              mt={3}
              p={2}
              bgcolor="#f5f5f5"
              borderRadius={2}
              role="note"
              aria-label="Security notice"
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="flex"
                alignItems="center"
              >
                <Iconify
                  icon="eva:shield-fill"
                  width={16}
                  sx={{ mr: 1, color: '#0F75BC' }}
                  aria-hidden="true"
                />
                Phone verification required for security
              </Typography>
            </Box>
          )}

          {/* Terms */}
          <Box mt={3} textAlign="center">
            <Typography variant="caption" color="text.secondary">
              By continuing, you agree to our{' '}
              <Typography
                component="a"
                href="/terms"
                variant="caption"
                color="primary"
                sx={{ textDecoration: 'none' }}
              >
                Terms of Service
              </Typography>{' '}
              and{' '}
              <Typography
                component="a"
                href="/privacy"
                variant="caption"
                color="primary"
                sx={{ textDecoration: 'none' }}
              >
                Privacy Policy
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Modal>

      {/* Email sign-up modal */}
      <EmailSignUpModal
        open={openSignUp}
        handleClose={handleCloseSignUp}
        onAccountCreated={handleSignUpWithVerification}
        requirePhoneVerification={requirePhoneVerification}
        onSignInInstead={handleSignInInstead}
        onResetPassword={handleResetFromSignUp}
      />

      {/* Phone Verification Modal */}
      <PhoneVerificationModal
        open={openPhoneVerification}
        handleClose={handleClosePhoneVerification}
        onVerificationComplete={handlePhoneVerificationComplete}
        userData={pendingUserData}
      />

      {/* Account Linking Modal */}
      <AccountLinkingModal
        open={accountLinkingOpen}
        onClose={handleCloseAccountLinking}
        email={accountLinkingData.email}
        newProvider={accountLinkingData.newProvider}
        onLinked={applySignedIn}
      />

      {/* Email Sign In. Dismissing it ends the flow; its back arrow returns
          to the chooser, which is what the arrow promises. */}
      <EmailSignInModal
        open={openSignIn}
        handleClose={() => {
          handleBackToChooser();
          handleClose();
        }}
        initialEmail={authEmail}
        onSignedIn={handleSignedIn}
        onForgotPassword={handleForgotPassword}
        onBack={handleBackToChooser}
      />

      {/* Forgot Password. Its back arrow returns to sign-in with the typed
          address intact; Done ends the flow. */}
      <ForgotPasswordModal
        open={openForgot}
        handleClose={() => {
          setOpenForgot(false);
          setAuthEmail('');
          handleClose();
        }}
        initialEmail={authEmail}
        onBackToSignIn={handleBackToSignIn}
      />
    </>
  );
}
